import { ctaClickHandler } from '@mnd-apps/public/lib/tracker';
import { formatUrl } from '@mnd-apps/public/utils/formatUrl';
import type { PageFragment } from '@mnd-apps/public/wp-generated/types';
import { Button } from '@mnd-frontend/ui/public/components/Button';
import {
  MEDIA_MEDIUM,
  MEDIA_SMALL,
  SPACING_1,
  SPACING_2,
  SPACING_4,
} from '@mnd-frontend/ui/shared/tokens';
import Image from 'next/image';
import styled from 'styled-components';

const NOTICE_BANNER_HEIGHT = '4.59375rem';
const NAVBAR_HEIGHT = '5.5rem';

const Section = styled.section<{ $noticeIsOpen?: boolean }>`
  position: relative;
  padding: clamp(5rem, 10vw, 10rem) var(--site-padding);
  height: ${({ $noticeIsOpen }) => ($noticeIsOpen ? `calc(100vh - ${NOTICE_BANNER_HEIGHT} - ${NAVBAR_HEIGHT})` : `calc(100vh - ${NAVBAR_HEIGHT})`)};
`;

const BackgroundImage = styled(Image)`
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    position: absolute;
    top: 0;
    left: 0;
    z-index: -2;
    display: block;
    @media (max-width: ${MEDIA_MEDIUM}px) {
      display: none;
    }
`;

const MobileImage = styled(BackgroundImage)`
  display: none;
  @media (max-width: ${MEDIA_MEDIUM}px) {
    display: block;
  }
`;

const Header = styled.header`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${SPACING_4};
  height: 100%;
  max-width: 36rem;
  @media (max-width: ${MEDIA_SMALL}px) {
    max-width: initial;
  }
`;

const TopSection = styled.div`
  display: grid;
  gap: ${SPACING_1};
`;

const BottomSection = styled.div`
  display: grid;
  gap: ${SPACING_2};
`;

const Heading = styled.h1`
  color: #ffffff;
  font-size: clamp(3rem, 8.5vw, 4.5rem);
  font-weight: 600;
  line-height: 100%;
  letter-spacing: -0.225rem;
  white-space: pre-line; //allow line breaks in title
`;

const SubHeading = styled.p`
  color: #ffffff;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: ${SPACING_2};
`;

const Footnote = styled.p`
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.125rem;
`;

const Hero = ({
  data,
  noticeIsOpen,
}: { data: NonNullable<PageFragment['hero']>['hero']; noticeIsOpen?: boolean }) => {
  const mobileImage = data?.mobileImage;
  return (
    <Section $noticeIsOpen={noticeIsOpen}>
      <BackgroundImage
        src={data?.image?.node?.mediaItemUrl ?? ''}
        alt={data?.image?.node?.altText ?? ''}
        width={data?.image?.node.mediaDetails?.width ?? 1920}
        height={data?.image?.node.mediaDetails?.height ?? 1080}
        priority
      />
      <MobileImage
        src={mobileImage?.node?.mediaItemUrl ?? data?.image?.node?.mediaItemUrl ?? ''}
        alt={mobileImage?.node?.altText ?? data?.image?.node?.altText ?? ''}
        width={data?.image?.node.mediaDetails?.width ?? 768}
        height={data?.image?.node.mediaDetails?.height ?? 1024}
        priority
      />
      <Header>
        <TopSection>
          <Heading>{data?.title}</Heading>
          <SubHeading>{data?.subTitle}</SubHeading>
        </TopSection>
        <BottomSection>
          {data?.links && data.links.length > 0 && (
            <ButtonsWrapper>
              {data.links.map((link, index) => (
                <Button
                  key={index}
                  newTab={link?.link?.target === '_blank'}
                  configuration={index === 0 ? 'primary' : 'tertiary'}
                  href={formatUrl(link?.link?.url ?? '')}
                  onClick={ctaClickHandler('hero')}
                >
                  {link?.link?.title ?? ''}
                </Button>
              ))}
            </ButtonsWrapper>
          )}
          {data?.footnote && <Footnote>{data.footnote}</Footnote>}
        </BottomSection>
      </Header>
    </Section>
  );
};

export default Hero;
